/**
 * Período dos relatórios no dia civil de Brasília (decisão G4, Fase 0).
 *
 * O campo de data devolve 'AAAA-MM-DD'. Antes, a tela mandava new Date('AAAA-MM-DD').toISOString(),
 * que é 00:00 UTC = 21h do dia ANTERIOR em Brasília: os relatórios começavam 3 h antes e o de
 * clientes multi-abastecimento mostrava linhas de um dia fora do período.
 *
 * Aqui o início é 00:00 e o fim é 23:59:59.999999 de Brasília, enviados como o instante em UTC no
 * MESMO formato que o banco grava ('2026-10-20T03:00:00+00:00'). Esse formato serve às duas famílias
 * de rota do backend: as que normalizam o período (services/dia_br.periodo_utc) e as que comparam o
 * texto ISO do banco direto (comparação de texto exige o mesmo formato; com '.000Z' o 1º milissegundo
 * do dia ficaria de fora). A data pura ('AAAA-MM-DD') não serve às do 2º grupo: 'criado_em <=
 * AAAA-MM-DD' perderia o último dia inteiro.
 *
 * Fuso fixo UTC-3 (sem horário de verão desde 2019), o mesmo do backend (OFFSET_BR em dia_br.py),
 * independente do fuso do aparelho do Master. Na URL, o '+' precisa ir codificado (%2B):
 * URLSearchParams e encodeURIComponent já fazem isso.
 */
const OFFSET_BR = "-03:00";
const DIA = /^\d{4}-\d{2}-\d{2}$/;

/** 'AAAA-MM-DD' + hora de Brasília -> 'AAAA-MM-DDTHH:MM:SS' em UTC (sem fração), ou null. */
function segundoUTC(dia, hora) {
  if (!DIA.test(String(dia || ""))) return null;
  const d = new Date(`${dia}T${hora}${OFFSET_BR}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 19);
}

/** 'AAAA-MM-DD' -> 00:00 de Brasília em UTC (ex.: '2026-10-20' -> '2026-10-20T03:00:00+00:00'). */
export function inicioDiaBR(dia) {
  const s = segundoUTC(dia, "00:00:00");
  return s ? `${s}+00:00` : null;
}

/** 'AAAA-MM-DD' -> 23:59:59.999999 de Brasília em UTC (ex.: '2026-10-20' -> '2026-10-21T02:59:59.999999+00:00'). */
export function fimDiaBR(dia) {
  const s = segundoUTC(dia, "23:59:59");
  return s ? `${s}.999999+00:00` : null;
}

/** Dia civil de Brasília agora ('AAAA-MM-DD'), independente do fuso do aparelho. */
export function hojeBR(agora = Date.now()) {
  return new Date(Number(agora) - 3 * 3600 * 1000).toISOString().slice(0, 10);
}

/** Soma n dias a 'AAAA-MM-DD' (negativo volta). Data inválida -> null. */
export function somaDiasBR(dia, n) {
  if (!DIA.test(String(dia || ""))) return null;
  const [y, m, d] = dia.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + Number(n || 0)));
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
}

/**
 * Atalhos de período (padrão do dono: abre em Hoje). Devolve {inicio, fim} em 'AAAA-MM-DD'.
 * chave: hoje | ontem | 7d | 30d | mes
 */
export function atalhoPeriodoBR(chave, agora = Date.now()) {
  const hoje = hojeBR(agora);
  switch (chave) {
    case "ontem": { const o = somaDiasBR(hoje, -1); return { inicio: o, fim: o }; }
    case "7d": return { inicio: somaDiasBR(hoje, -6), fim: hoje };
    case "30d": return { inicio: somaDiasBR(hoje, -29), fim: hoje };
    case "mes": return { inicio: `${hoje.slice(0, 8)}01`, fim: hoje };
    default: return { inicio: hoje, fim: hoje };
  }
}
